import React from "react";
import { useDispatch } from "react-redux";
import ProgressDonut from "../../insight/components/charts/ProgressDonut";
import { useAiContentTokenQuota } from "../hooks/useAiContentTokenQuota";
import { showAiContentQuotaUpgrade } from "../utils/aiContentQuotaUtils";

const DONUT_SIZE = 28;
const DONUT_STROKE = 3;

function quotaDonutColor(percentUsed: number, isExhausted: boolean): string {
  if (isExhausted) return "#EF4444";
  if (percentUsed >= 85) return "#F59E0B";
  return "#EAB308";
}

/**
 * Informational AI token meter only — does not gate access to Content Studio.
 */
const AiContentTokenUsageBar: React.FC = () => {
  const dispatch = useDispatch();
  const { totalTokens, limit, hasAllowance, isExhausted, percentUsed } =
    useAiContentTokenQuota();

  const donutColor = hasAllowance
    ? quotaDonutColor(percentUsed, isExhausted)
    : "#D1D5DB";
  const displayPercent = hasAllowance ? percentUsed : 0;

  const donut = (
    <ProgressDonut
      percent={displayPercent}
      size={DONUT_SIZE}
      strokeWidth={DONUT_STROKE}
      color={donutColor}
      trackColor="#E5E7EB"
      ariaLabel={
        hasAllowance
          ? `${percentUsed}% — ${totalTokens.toLocaleString()} / ${limit.toLocaleString()}`
          : "AI token usage loading"
      }
    >
      {hasAllowance ? (
        <span className="text-[8px] font-semibold leading-none text-gray-800">{percentUsed}%</span>
      ) : null}
    </ProgressDonut>
  );

  if (isExhausted) {
    return (
      <button
        type="button"
        onClick={() => showAiContentQuotaUpgrade(dispatch)}
        className="shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
        title={`${totalTokens.toLocaleString()} / ${limit.toLocaleString()}`}
      >
        {donut}
      </button>
    );
  }

  return (
    <div
      className="shrink-0"
      title={
        hasAllowance ? `${totalTokens.toLocaleString()} / ${limit.toLocaleString()}` : undefined
      }
    >
      {donut}
    </div>
  );
};

export default AiContentTokenUsageBar;
